import React, { useCallback, useContext, useEffect, useState } from 'react';
import { NotificationOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, PageHeader, Modal, Layout, Table } from 'antd';
import { hasPermission, apiClient, apiErrorMessage } from '../../utils';
import { UserContext } from '../contexts';
import { QaErrorEditPage } from './qa-error-edit-page';
import { QaError, ErrorCode, NotificationType } from '../../models/index';
import { EntityChangeLog } from '../reports';
import { errorTypes } from '../../models/enums/error-type';
import { QaSubErrorsPage } from './qa-sub-errors-page';

export function QaMatrixPage() {
  const { Content } = Layout;
  const [loading, setLoading] = useState(false);
  const { user } = useContext(UserContext);
  const [canEdit, setCanEdit] = useState(false);
  const [qaErrors, setQaErrors] = useState<(QaError & { key: string })[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalQaError, setEditModalQaError] = useState<QaError>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'matrices.edit'));
  }, [user]);

  const loadQaErrors = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`qa-errors`)
      .then((response: any) => {
        const responseCosts: QaError[] = response.data;
        const responseData = responseCosts.map(subError => {
          return {
            key: subError.id,
            ...subError,
          };
        });
        setQaErrors(responseData);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadQaErrors();
  }, [loadQaErrors]);

  const onRefreshClick = () => {
    loadQaErrors();
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalQaError(undefined);
  };

  const onQaErrorEditSaved = () => {
    closeEditModal();
    loadQaErrors();
  };

  const openEditModal = () => {
    setEditModalQaError(undefined);
    setEditModalOpen(true);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableMainColumns: any[] = [
    {
      title: 'Hata no',
      dataIndex: 'errorNo',
      key: 'errorNo',
    }, {
      title: 'Bildirim türü',
      dataIndex: 'notificationType',
      key: 'notificationType',
      render: (notificationType: NotificationType) => notificationType.name,
    }, {
      title: 'İlk bildirim',
      dataIndex: '',
      key: '',
    }, {
      title: 'Hata türü',
      dataIndex: 'notificationType',
      key: 'notificationType',
      render: (notificationType: NotificationType) => errorTypes.get(notificationType.errorType),
    }, {
      title: 'Hata kodu',
      dataIndex: 'errorCode',
      key: 'errorCode',
      render: (errorCode: ErrorCode) => errorCode.code,
    }, {
      title: 'Problem açıklaması',
      dataIndex: 'errorCode',
      key: 'errorCode',
      render: (errorCode: ErrorCode) => errorCode.description,
    }, {
      title: 'Toplam maliyet puanı',
      dataIndex: 'totalCostScore',
      key: 'totalCostScore',
    }, {
      title: 'Toplam ağırlık puanı',
      dataIndex: 'totalWeightPoint',
      key: 'totalWeightPoint',
    }, {
      title: 'Toplam frekans puanı',
      dataIndex: 'totalFrequencyScore',
      key: 'totalFrequencyScore',
    }];

  const getYearColumns = (): any[] => {
    const columns: any[] = [];
    qaErrors?.forEach((q => {
      q.yearFrequencies.forEach((f => {
        if (!columns.includes(f.year))
          columns.push(f.year)
      }))
    }))
    return columns.sort((a, b) => a - b);
  }

  const getFrequenciesColumns = (): any[] => {
    const columns: any[] = [];
    getYearColumns().forEach((row: any) => {
      columns.push({
        title: row,
        dataIndex: "yearFrequencies",
        key: "yearFrequencies",
        render: (yearFrequencies: any) => yearFrequencies.filter((y: any) => y.year === row)[0]?.numberOfViews,
      })
    })
    return columns;
  }

  const tableColumns = tableMainColumns.concat(getFrequenciesColumns());

  return (
    <>
      <PageHeader
        title={
          <>
            <NotificationOutlined /> QA Matris
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" icon={<PlusOutlined />} onClick={openEditModal} type="primary">
                Yeni hata kaydı
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table expandable={{
          expandedRowRender: record => <QaSubErrorsPage errorId={record.id} canEdit={canEdit} onRefresh={onRefreshClick} />,
          rowExpandable: record => true,
        }} columns={tableColumns} dataSource={qaErrors} loading={loading} pagination={false} scroll={{ x: 1300 }} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'QaError'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal title="Ana hata kaydı" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <QaErrorEditPage id={editModalQaError?.id} onSaved={onQaErrorEditSaved} onCancel={closeEditModal} />
      </Modal>
    </>
  );
}
